import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatest, fromEvent, map, Observable, of, tap, startWith } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { TranslatedBenefitRow } from '@fcom/dapi/api/models/translated-benefit-row';
import { TagRoundingStyle, TagTheme } from '@fcom/ui-components/components/tag/interfaces';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { shouldDisplayBenefit, shouldDisplayWifiBenefit } from '../../../utils/benefit.utils';
import { ExtendedFareFamily, FareFamilyBadgeType } from '../../../interfaces/flight-selection.interface';
import { TaggableComponent } from '../../../../ui-components/components/tag/taggable.component';
import { badgeTypes } from '../badges/badges';

@Component({
  selector: 'fin-fare-family-list-option',
  styleUrls: ['./fare-family-list-option.component.scss'],
  templateUrl: './fare-family-list-option.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlightSelectionFareFamilyListOptionComponent extends TaggableComponent implements OnInit {
  configService = inject(ConfigService);
  mediaQueryService = inject(MediaQueryService);
  private el = inject(ElementRef);
  private themeService = inject(FinnairThemeService);

  destroyRef = inject(DestroyRef);

  readonly FareFamilyBadgeType = FareFamilyBadgeType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagRoundingStyle = TagRoundingStyle;
  readonly TagTheme = TagTheme;
  readonly badgeTypes = badgeTypes;

  @Input({ required: true }) fareFamily$: Observable<ExtendedFareFamily>;
  @Input({ required: true }) currencyCode: string;
  @Input({ required: true }) aria: {
    index: number;
    count: number;
  };

  @Input() isShowTotalPrice = false;
  @Input() showKoreaNotification: boolean;
  @Input() hasWideBodyAircraft$: Observable<boolean> = of(false);

  @Output() selectFareFamily = new EventEmitter<boolean>();

  selected$: Observable<boolean> = this.selectFareFamily.asObservable().pipe(startWith(false));

  fareFamilyBenefit$: Observable<TranslatedBenefitRow[]>;
  isNotAward$: Observable<boolean>;
  isHover$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  tagTheme$: Observable<TagTheme>;

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  ngOnInit(): void {
    fromEvent(this.el.nativeElement, 'mouseenter')
      .pipe(
        tap(() => this.isHover$.next(true)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    fromEvent(this.el.nativeElement, 'mouseleave')
      .pipe(
        tap(() => this.isHover$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.isNotAward$ = this.fareFamily$.pipe(
      map((fareFamily) => {
        const isAward = Boolean(fareFamily.points);
        return !isAward;
      })
    );

    this.fareFamilyBenefit$ = combineLatest([this.fareFamily$, this.hasWideBodyAircraft$]).pipe(
      map(
        ([
          {
            benefits: { translatedBenefitRows },
          },
          hasWideBodyAircraft,
        ]) => {
          return translatedBenefitRows
            .filter((benefit) => shouldDisplayBenefit(benefit))
            .filter((benefit) => shouldDisplayWifiBenefit(benefit, hasWideBodyAircraft));
        }
      )
    );

    this.tagTheme$ = combineLatest([this.isHover$, this.selected$, this.fareFamily$]).pipe(
      map(([isHover, selected, fareFamily]) => {
        if (selected) {
          return TagTheme.DEFAULT;
        }

        if (isHover) {
          return TagTheme.DEFAULT;
        }

        return fareFamily.benefits.tags[0].theme as TagTheme;
      }),
      startWith(TagTheme.ACTIVE)
    );
  }

  checkboxChange(event: Event): void {
    this.selectFareFamily.emit((event.target as HTMLInputElement).checked);

    if (!this.mediaQueryService.isBreakpoint('tablet_down')()) {
      return;
    }

    const fareFamilyElement = (event.target as HTMLInputElement).closest('.fare-family');

    if (this.isHalfFareFamilyVisible(fareFamilyElement)) {
      fareFamilyElement.querySelector('label').scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }

  private isHalfFareFamilyVisible(element: Element): boolean {
    const rect = element.getBoundingClientRect();
    return element.clientHeight / 2 < rect.top;
  }
}
